import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import {
  ChartBarIcon,
  ChevronDownIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  EyeIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  RocketLaunchIcon,
  Squares2X2Icon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import Logo from "./Logo";
import SidebarNavItem from "./SidebarNavItem";
import MoreMenu from "./MoreMenu";
import { setCurrentProject } from "../redux/project/projectSlice";
import { setBetaExpanded } from "../redux/ui/uiSlice";
import { projectsService } from "../services/projects";
import { colorFromName } from "../utils/color";
import { SIDEBAR_BADGES_REFRESH_EVENT } from "../utils/sidebarBadges";
import { sitePath, siteSlugOf } from "../utils/urls";

const Sidebar = ({ isOpen, onClose, currentUser }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentProject, projects, loaded: projectsLoaded } = useSelector(
    (state) => state.project
  );
  const onApiPage = location.pathname.startsWith("/api/");
  const { sidebarCollapsed, betaExpanded } = useSelector((state) => state.ui);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [sidebarBadges, setSidebarBadges] = useState({
    changes_need_attention: 0,
    changes_running: false,
  });
  const desktopProjectMenuRef = useRef(null);
  const mobileProjectMenuRef = useRef(null);

  const projectSelectorLoading = !projectsLoaded;
  const showProjectSearch = projects.length > 10;
  const filteredProjects = !projectSearch.trim()
    ? projects
    : projects.filter((project) =>
        (project.name || "").toLowerCase().includes(projectSearch.trim().toLowerCase()),
      );

  useEffect(() => {
    if (!currentProject?.hash) {
      setSidebarBadges({ changes_need_attention: 0, changes_running: false });
      return undefined;
    }

    const loadSidebarBadges = () => {
      projectsService.getSidebarBadges(currentProject.hash, (response) => {
        setSidebarBadges(response.data);
      });
    };

    loadSidebarBadges();
    const ms = sidebarBadges.changes_running ? 3000 : 30000;
    const timer = setInterval(loadSidebarBadges, ms);
    window.addEventListener(SIDEBAR_BADGES_REFRESH_EVENT, loadSidebarBadges);
    return () => {
      clearInterval(timer);
      window.removeEventListener(SIDEBAR_BADGES_REFRESH_EVENT, loadSidebarBadges);
    };
  }, [currentProject?.hash, sidebarBadges.changes_running]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDesktop = desktopProjectMenuRef.current?.contains(event.target);
      const inMobile = mobileProjectMenuRef.current?.contains(event.target);
      if (!inDesktop && !inMobile) {
        setProjectMenuOpen(false);
        setProjectSearch("");
      }
    };
    if (projectMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [projectMenuOpen]);

  const handleProjectLinkClick = (event, project) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    // API has no /site/:slug route; stay put and swap the selected site for playground prefills.
    if (onApiPage) {
      event.preventDefault();
      dispatch(setCurrentProject(project));
    }
    setProjectMenuOpen(false);
    setProjectSearch("");
    onClose?.();
  };

  const projectHref = (project) => {
    if (onApiPage) {
      return { pathname: location.pathname, search: location.search };
    }
    const match = location.pathname.match(/^\/site\/[^/]+(\/.*)$/);
    // Checkpoint/env query params are site-specific; land on the section root.
    // Report detail hashes are site-specific too; drop to the reports list.
    let suffix = match?.[1];
    if (suffix?.match(/^\/seo\/reports\/[^/]+\/?$/)) {
      suffix = "/seo/reports/";
    }
    return {
      pathname: suffix ? `/site/${siteSlugOf(project)}${suffix}` : sitePath(project, "preview/"),
      search: "",
    };
  };

  const handleAddProject = () => {
    setProjectMenuOpen(false);
    onClose?.();
  };

  const siteNavDisabled = !currentProject;
  const siteSlug = siteSlugOf(currentProject);

  const isNavActive = (href) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    if (href === "/account/") {
      return location.pathname.startsWith("/account/");
    }
    if (href.includes("/settings/")) {
      return location.pathname.includes("/settings/");
    }
    return location.pathname === href || location.pathname.startsWith(href);
  };

  const mainNavigation = [
    { name: "Preview", href: sitePath(siteSlug, "preview/"), icon: <EyeIcon className="h-4 w-4" /> },
    {
      name: "Changes",
      href: sitePath(siteSlug, "changes/"),
      icon: <PencilSquareIcon className="h-4 w-4" />,
      badge: sidebarBadges.changes_need_attention,
      badgeSpinner: sidebarBadges.changes_running,
    },
    { name: "Tables", href: sitePath(siteSlug, "data/"), icon: <CircleStackIcon className="h-4 w-4" /> },
    { name: "Settings", href: sitePath(siteSlug, "settings/general/"), icon: <Cog6ToothIcon className="h-4 w-4" /> },
  ];

  const betaNavigation = [
    { name: "Analytics", href: sitePath(siteSlug, "analytics/"), icon: <ChartBarIcon className="h-4 w-4" /> },
    { name: "Audits", href: sitePath(siteSlug, "seo/"), icon: <ClipboardDocumentCheckIcon className="h-4 w-4" /> },
    { name: "Media", href: sitePath(siteSlug, "media/"), icon: <PhotoIcon className="h-4 w-4" /> },
    { name: "Deploys", href: sitePath(siteSlug, "deploys/"), icon: <RocketLaunchIcon className="h-4 w-4" /> },
  ];

  const betaSection = (collapsed) => {
    if (betaExpanded) {
      return betaNavigation.map((item) => (
        <SidebarNavItem
          key={item.name}
          item={item}
          isActive={!siteNavDisabled && isNavActive(item.href)}
          disabled={siteNavDisabled}
          onClick={onClose}
          collapsed={collapsed}
        />
      ));
    }
    const className = `w-full flex items-center text-[13px] font-medium rounded-lg ${
      collapsed ? "justify-center px-2 py-2" : "px-2.5 py-2"
    } ${
      siteNavDisabled
        ? "text-theme-text opacity-30 cursor-not-allowed"
        : "text-theme-text opacity-55 hover:opacity-90 hover:bg-black hover:bg-opacity-5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-theme-primary"
    }`;
    const content = (
      <>
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
        {!collapsed && <span className="ml-2.5">Beta</span>}
      </>
    );
    return (
      <>
        {siteNavDisabled ? (
          <span aria-disabled="true" className={className} data-tooltip-id={collapsed ? "beta-tooltip" : undefined} data-tooltip-content={collapsed ? "Beta" : undefined}>
            {content}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => dispatch(setBetaExpanded(true))}
            className={className}
            aria-label="Show beta navigation"
            data-tooltip-id={collapsed ? "beta-tooltip" : undefined}
            data-tooltip-content={collapsed ? "Beta" : undefined}
          >
            {content}
          </button>
        )}
        {collapsed && <Tooltip id="beta-tooltip" place="right" className="z-50" />}
      </>
    );
  };

  const bottomNavigation = [
    { name: "Sites", href: "/", icon: <Squares2X2Icon className="h-4 w-4" /> },
    {
      name: "Account",
      href: "/account/",
      icon: (
        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
        </svg>
      ),
    },
  ];

  const projectSelector = (collapsed, menuRef) => (
    <div
      ref={menuRef}
      className={collapsed ? "px-2 pb-3" : "px-3 pb-3"}
    >
      <div className="relative">
        <button
          type="button"
          onClick={() => setProjectMenuOpen((open) => !open)}
          className={`group w-full flex items-center rounded-lg border border-transparent text-left text-theme-text transition hover:bg-black hover:bg-opacity-5 hover:text-theme-primary ${
            collapsed ? "justify-center px-2 py-2" : "px-2.5 py-2"
          } ${currentProject ? "bg-black bg-opacity-5" : ""}`}
          data-tooltip-id={collapsed ? "project-tooltip" : undefined}
          data-tooltip-content={collapsed ? currentProject?.name || "Select project" : undefined}
        >
          {currentProject ? (
            <ProjectIcon project={currentProject} />
          ) : (
            <FolderIcon className="h-4 w-4 flex-shrink-0 text-theme-text opacity-50" />
          )}
          {!collapsed && (
            <span
              className={`ml-2 flex-1 truncate text-[13px] font-semibold ${
                currentProject?.is_archived ? "text-theme-text opacity-40" : ""
              }`}
            >
              {projectSelectorLoading
                ? "Loading..."
                : currentProject?.name || "No site selected"}
            </span>
          )}
          {!collapsed && (
            <svg className={`h-4 w-4 opacity-60 transition-transform ${projectMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          )}
        </button>
        {collapsed && <Tooltip id="project-tooltip" place="right" className="z-50" />}

        {projectMenuOpen && (
          <div
            className={`absolute z-50 rounded-lg border border-theme-secondary bg-theme-main shadow-lg ${
              collapsed
                ? "left-full ml-2 top-0 mt-0 w-56"
                : "left-0 right-0 top-full mt-2"
            }`}
          >
            {showProjectSearch && (
              <div className="px-2.5 pt-2 pb-0.5">
                <label className="relative block">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-theme-text opacity-40" />
                  <input
                    type="search"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Search sites"
                    autoFocus
                    className="w-full bg-transparent py-1 pl-6 pr-1 text-xs text-theme-text placeholder:opacity-40 focus:outline-none"
                  />
                </label>
              </div>
            )}
            <div className="max-h-64 overflow-y-auto py-0.5">
              {filteredProjects.map((project) => {
                const isCurrent =
                  currentProject?.hash === project.hash || currentProject?.id === project.id;
                return (
                  <Link
                    key={project.id || project.hash}
                    to={projectHref(project)}
                    onClick={(event) => handleProjectLinkClick(event, project)}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-theme-text transition-colors duration-150 hover:bg-black hover:bg-opacity-5 ${
                      isCurrent ? "font-semibold" : ""
                    }`}
                  >
                    <ProjectIcon project={project} />
                    <span
                      className={`truncate ${
                        project.is_archived ? "text-theme-text opacity-40" : ""
                      }`}
                    >
                      {project.name || "Unnamed project"}
                    </span>
                  </Link>
                );
              })}
              {!filteredProjects.length && !projectSelectorLoading ? (
                <p className="px-3 py-2 text-sm text-theme-text opacity-60">
                  {projectSearch.trim() ? "No matching sites" : "No projects yet"}
                </p>
              ) : null}
            </div>
            <div className="border-t border-theme-secondary py-1">
              <Link
                to="/onboarding/"
                onClick={handleAddProject}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-theme-primary transition hover:opacity-70"
              >
                <PlusIcon className="h-4 w-4 flex-shrink-0" />
                Add site
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:flex lg:flex-shrink-0 overflow-visible h-screen">
        <div
          className={`flex flex-col h-full border-r border-theme-secondary bg-theme-secondary transition-all duration-300 overflow-visible ${
            sidebarCollapsed ? "w-16" : "w-56"
          }`}
        >
          <div
            className={`flex items-center h-14 ${
              sidebarCollapsed ? "justify-center px-2" : "pl-[22px] pr-4"
            }`}
          >
            <Link
              to="/"
              className="flex items-center cursor-pointer"
              data-tooltip-id="logo-tooltip"
              data-tooltip-content="Weave"
            >
              <Logo className="h-4 w-[42px] flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="ml-2.5 text-[14.5px] font-bold text-theme-text">
                  Weave
                </span>
              )}
            </Link>
            {sidebarCollapsed && (
              <Tooltip id="logo-tooltip" place="right" className="z-50" />
            )}
          </div>

          {projectSelector(sidebarCollapsed, desktopProjectMenuRef)}

          <nav className={`space-y-0.5 overflow-visible ${sidebarCollapsed ? "px-2" : "px-3"}`}>
            {mainNavigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                isActive={!siteNavDisabled && isNavActive(item.href)}
                disabled={siteNavDisabled}
                onClick={onClose}
                collapsed={sidebarCollapsed}
              />
            ))}
            {betaSection(sidebarCollapsed)}
          </nav>

          <div className={`mt-auto py-3 space-y-0.5 overflow-visible ${sidebarCollapsed ? "px-2" : "px-3"}`}>
            {bottomNavigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                isActive={isNavActive(item.href)}
                onClick={onClose}
                collapsed={sidebarCollapsed}
              />
            ))}
            <div className="pt-1">
              <MoreMenu
                collapsed={sidebarCollapsed}
                onClose={onClose}
                currentUser={currentUser}
              />
            </div>
          </div>
        </div>
      </aside>

      <aside
        className={`fixed inset-y-0 left-0 z-30 w-56 border-r border-theme-secondary bg-theme-secondary transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-14 pl-[22px] pr-4">
            <Link to="/" className="flex items-center cursor-pointer">
              <Logo className="h-4 w-[42px] flex-shrink-0" />
              <span className="ml-2.5 text-[14.5px] font-bold text-theme-text">
                Weave
              </span>
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md text-theme-text opacity-70 hover:opacity-100 hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-theme-primary"
              aria-label="Close sidebar"
            >
              <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {projectSelector(false, mobileProjectMenuRef)}

          <nav className="space-y-0.5 px-3 overflow-y-auto">
            {mainNavigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                isActive={!siteNavDisabled && isNavActive(item.href)}
                disabled={siteNavDisabled}
                onClick={onClose}
              />
            ))}
            {betaSection(false)}
          </nav>

          <div className="mt-auto px-3 py-3 space-y-0.5">
            {bottomNavigation.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                isActive={isNavActive(item.href)}
                onClick={onClose}
              />
            ))}
            <div className="pt-1">
              <MoreMenu
                showSidebarToggle={false}
                onClose={onClose}
                currentUser={currentUser}
              />
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

function ProjectIcon({ project }) {
  if (project?.is_archived) {
    // Same alpha pattern as colorFromName(name) / colorFromName(name, 0.1), but grey.
    const grey = "hsl(0 0% 42%)";
    return (
      <span
        className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded"
        style={{ color: grey, backgroundColor: "hsl(0 0% 42% / 0.1)" }}
        aria-hidden="true"
      >
        <TrashIcon className="h-2.5 w-2.5" />
      </span>
    );
  }
  if (project?.favicon_url) {
    return (
      <img
        src={project.favicon_url}
        alt=""
        className="h-4 w-4 flex-shrink-0 rounded object-cover"
      />
    );
  }
  const name = project?.name || "?";
  const letter = name.trim().charAt(0).toUpperCase() || "?";
  const color = colorFromName(name);
  return (
    <span
      className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded text-[10px] font-semibold leading-none"
      style={{ color, backgroundColor: colorFromName(name, 0.1) }}
      aria-hidden="true"
    >
      {letter}
    </span>
  );
}

export default Sidebar;
